import React, {
  useCallback,
  useEffect,
  useState,
  useContext,
} from "react";
import {
  Box,
  Typography,
} from "@mui/material";
import { styled } from "@mui/styles";
import { LayoutBodyContext } from "layout/LayoutBodyContext";
import {
  colors,
  margin,
  padding,
} from "theme/defaultStyle";
import PageSection from "controls/global/page-section";
import { InventoryModifySearchCriteria } from "entities/UIModel/InventoryModifySearchCriteria";
import { Order } from "utils/sorting";
import { useUser } from "utils/context/UserContext";
import {
  orderBy as _orderBy,
  debounce
} from "lodash";
import { useConfigContext } from "utils/context/ConfigContextProvider";
import { DateTypeCode } from "utils/data/enum";
import ScrollToTopArrow from "controls/global/scroll-to-top-arrow";
import { IColumn } from "entities/ApiModel/IColumn";
import { InventoryModifyExportCriteria } from "entities/ApiModel/InventoryModifyExportCriteria";
import { getColumnSearchField } from "utils/columnsGrid";
import InventoryModifyGrid from "./InventoryModifyGrid";
import InventoryModifyHeader from "./InventoryModifyHeader";
import IosShareIcon from '@mui/icons-material/IosShare';
import { InventoryModifyUI } from "entities/UIModel/InventoryModifyUI";
import { SelectionConfig, StewartTableColumn } from "controls/global/stewart-table/StewartTable";
import useModifyStore from "utils/context/InventoryModifyContext";
import FileExportDialog from "controls/global/file-export-dialog/FileExportDialog";
import { EmptyStateType } from "controls/global/stewart-table/StewartTableBodyEmpty";
const StyledBoxContainer = styled(Box)({
  position: "relative",
  "& .MuiOutlinedInput-input": {
    marginBottom: margin.xxsmall2,
    marginLeft: margin.small2,
    marginTop: margin.small,
    padding: `${padding.small} ${padding.small2}`,
  },
  "&.image-archive > div > div:nth-child(2):not(.MuiInputAdornment-root)": {
    boxShadow: "none",
    paddingTop: "10px",
  },
  "& div.MuiTableContainer-root": {
    marginTop: `-${margin.medium}`,
  },
  "& div.grid-edit-mode": {
    "& > div.MuiBox-root": {
      color: colors.grey11,
      "& .MuiPaginationItem-root:not(.MuiPaginationItem-ellipsis)": {
        color: colors.grey11,
      },
      "& .MuiPaginationItem-root:not(.MuiPaginationItem-ellipsis).Mui-selected": {
        backgroundColor: colors.grey10,
      }
    }
  }
});

const TableGridHeaderContainer = styled(Box)({
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
  width: "100%",
})

const ButtonContainer = styled(Box)({
  flexShrink: 0,
  display: "flex",
  alignItems: "flex-end",
})

const SubHeadingText = styled(Typography)({
  flex: 1,
  minWidth: 0,
  pr: 2,
  whiteSpace: "normal",
  wordBreak: "break-word",
})

const InventoryModify = () => {
  const [initialLoad, setInitialLoad] = useState(true);
  const InitialMessage = "Loading results...";
  const NoRecordsMessage = "No results Found";
  const TimeoutMessage = "Sorry, this search took too long. Please modify your search criteria and try again.";
  const [page, setPage] = useState(1);
  const [data, setData] = useState<InventoryModifyUI[]>();
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [
    { selectedModifyItems, isAdvanceSearch, error: requestError, progressPercent: loadingProgressPercent, refreshData, stateList, statusList, policyTypeList, prefixList },
    {
      getSearchDocuments,
      getInitialColumnsDefintion,
      getColumnsDefinition,
      setColumnDefinition,
      updateSelectedModifyItems,
      exportFiles,
      updateSelectedModifyItem,
      resetSelectedModifyItem,
      setAdvanceSearch,
      cancelRequests,
      getLookups,
    },
  ] = useModifyStore();
  const [readyToSearch, setReadyToSearch] = useState<boolean>(false);
  const [isAdvSearchOpen, setIsAdvSearchOpen] = useState<boolean>(false);
  const { generalConfig } = useConfigContext();

  const [order, setOrder] = React.useState<Order>("desc");
  const [orderBy, setOrderBy] = React.useState<keyof InventoryModifyUI | undefined>("inventoryModifiedDate");
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const [filters, setFilters] = useState<InventoryModifySearchCriteria | undefined>(undefined);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [partialFilterUpdate, setPartialFilterUpdate] = useState<boolean>(false);
  const [defaultOpenedDate, setDefaultOpenedDate] = useState<Date>(new Date());
  const [userInputDebounceTimeMs, setUserInputDebounceTimeMs] = useState<number>(750);
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false);
  const [openExportDialog, setOpenExportDialog] = useState<boolean>(false);

  const [, { getRowsPerPageSetting, setRowsPerPageSetting }] = useUser();
  const [emptyStateType, setEmptyStateType] = useState<EmptyStateType>(EmptyStateType.DEFAULT);
  const [noRecordsMessage, setNoRecordsMessage] = useState<string[]>([InitialMessage]);
  const [columns, setColumns] = useState<IColumn[]>(getInitialColumnsDefintion(false));
  const [hiddenColumns, setHiddenColumns] = useState<(keyof InventoryModifyUI)[]>(getInitialColumnsDefintion(true));
  const [exportedColumns, setExportedColumns] = useState<string[]>([]);
  const bodyRef = useContext(LayoutBodyContext);
  const getDatabaseSortColumnName = (name: string) => {
    const mappedColumnNames: { [key: string]: string } = {
      documentDisplayFileName: "DocumentDisplayFileName",
      companyName: "CompanyName",
      newCompanyName: "CompanyName",
      newLocationDisplayName: "LocationDisplayName",
      formPrefix: "FormPrefix",
      inventoryModifyID: "TicketNumber",
      inventoryModifiedDate: "InventoryModifiedDate",
      companyId: "CompanyId",
      documentUploadedByUserName: "DocumentUploadedByUserName",
      documentUploadedDate: "DocumentUploadedDate",
      rowsPerPage: "RowsPerPage",
      currentRow: "CurrentRow",
      currentPage: "CurrentPage",
      totalPages: "TotalPages",
      totalRows: "TotalRows",
      newLocationStateAbbr: "LocationStateAbbr",
      newLocationLegacyID: "LocationLegacyID",
      newCompanyLegacyID: "CompanyLegacyID",
      documentSize: "DocumentSize",
      documentFileTypeCode: "DocumentFileTypeCode",
      clientFileID: "ClientFileID",
      serialNumberStatusTypeName: "SerialNumberStatusTypeName",
    };
    return mappedColumnNames[name] ?? name;
  };

  const getFileSearchCriteria = () => {
    let criteria: InventoryModifySearchCriteria;

    criteria = {
      ...filters,
      listCount: rowsPerPage,
      requestedPage: page,
      dateTypeCode: isAdvSearchOpen ? DateTypeCode.Modified : undefined,
      isInitialLoad: initialLoad,
    };
    if (sortColumn) {
      criteria = {
        ...criteria,
        sortColumn: sortColumn,
      };
    }

    return criteria;
  };

  const loadSettings = async () => {
    const debounceTime = generalConfig.userInputDebounceTimeMs;
    const filterDays = generalConfig.filesSearchDefaultLookupDays;
    // const timeoutMs = generalConfig.filesSearchTimeoutMs;

    if (debounceTime) {
      setUserInputDebounceTimeMs(debounceTime);
    }
    if (filterDays) {
      setDefaultOpenedDate(new Date(new Date().getTime() - filterDays * 24 * 60 * 60 * 1000));
    }

  };

  const getDefaultFilters = (advanced: boolean): InventoryModifySearchCriteria => {
    const sortColumn = "-InventoryModifiedDate";
    const dateType = advanced ? DateTypeCode.Modified : undefined;

    const defaultFilters: InventoryModifySearchCriteria = {
      listCount: rowsPerPage,
      dateTypeCode: dateType,
      requestedPage: 1,
      sortColumn: sortColumn,
    };
    return defaultFilters;
  };

  const performSearch = async () => {
    let criteria = getFileSearchCriteria();
    criteria.isInitialLoad = false;
    if (initialLoad) {
      criteria.isInitialLoad = true;
      setInitialLoad(false);
    }
    let files: InventoryModifyUI[] | undefined = [];
    try {
      files = await getSearchDocuments({ ...criteria });
      if (files !== undefined) {
        setData(files);
        setEmptyStateType(EmptyStateType.DEFAULT);
        if (!files?.length) {
          setEmptyStateType(EmptyStateType.NO_RESULTS);
          setNoRecordsMessage([NoRecordsMessage]);
          setPage(1);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleRowsPerPageChange = async (count: number) => {
    await cancelRequests();
    setRowsPerPageSetting(count);
    setRowsPerPage(count);
    setPartialFilterUpdate(false);
    setPage(1);
  };


  const handleToggleAllRows = async () => {
    if (data) {
      updateSelectedModifyItems(data);
    }
  };

  const handleToggleRow = (document: InventoryModifyUI) => {
    updateSelectedModifyItem(document);
  };

  const selectionConfig: SelectionConfig<InventoryModifyUI> = {
    enabled: true,
    selectedRows: selectedModifyItems?.map((d) => d.uniqueIdentifier) ?? [],
    onToggleAllRows: handleToggleAllRows,
    onToggleRow: handleToggleRow,
    isRowSelected: (document: InventoryModifyUI) =>
      selectedModifyItems?.some((d) => d.uniqueIdentifier === document.uniqueIdentifier),
  };

  const handleAdvancedFilterChange = (
    name: keyof InventoryModifySearchCriteria,
    value: any,
    shouldSearch: boolean
  ) => {
    if (isAdvSearchOpen) {
      setPartialFilterUpdate(!shouldSearch);
      handleFilterChange(name, value);
    }
    else {
      handleFilterChange(name, null);
    }

  };

  const handleAdvanceSearchPanelOpen = async (open: boolean) => {
    await cancelRequests();
    setIsAdvSearchOpen(open);
    setReadyToSearch(true);
    setPage(1);
    setAdvanceSearch(open);
    const resetFilters = () => {
      return getDefaultFilters(open);
    };
    setFilters(resetFilters);
  };


  const handlePageChange = async (page: number) => {
    await cancelRequests();
    setPage(page);
  };

  useEffect(() => {
    if (readyToSearch) {
      const search = async () => {
        await performSearch();
        setReadyToSearch(false);
      };

      search();
      getLookups();
    }
  }, [readyToSearch]);

  const canSearch = (): boolean => {
    return !partialFilterUpdate && filters !== undefined && Object.values(filters).length > 0;
  };

  const debounceSetReadyToSearch = useCallback(
    debounce(() => setReadyToSearch(true), userInputDebounceTimeMs),
    [userInputDebounceTimeMs],
  );

  useEffect(() => {
    if (canSearch()) {
      if (partialFilterUpdate) {
        setPartialFilterUpdate(false);
      }

      debounceSetReadyToSearch();
    }
  }, [
    filters,
    page,
    sortColumn,
    filtersApplied,
    settingsLoaded,
    columns,
    rowsPerPage,
    partialFilterUpdate,
    isAdvanceSearch,
    refreshData,
  ]);

  useEffect(() => {
    const defaultFilters = getDefaultFilters(false);
    setFilters(defaultFilters);
  }, [defaultOpenedDate]);


  const getSearchField = (field: keyof InventoryModifyUI) =>
    getColumnSearchField<InventoryModifyUI, InventoryModifySearchCriteria>(columns, field);

  const handleRequestSort = async (columnName: any, disableToggling: boolean = false) => {
    await cancelRequests();
    let sortBy = getDatabaseSortColumnName(columnName);
    if (!disableToggling && sortColumn === sortBy) {
      sortBy = `-${sortBy}`;
    }

    setOrder(sortBy.startsWith("-") ? "desc" : "asc");
    setOrderBy(columnName.replace("-", "") as keyof InventoryModifyUI);
    setSortColumn(sortBy);
  };

  const handleFilterChange = async (name: keyof InventoryModifySearchCriteria, value: any) => {
    await cancelRequests();
    const formattedValue = typeof value === "string" ? value.replaceAll(",", "|") : value;
    const updatedFilters = (filters: InventoryModifySearchCriteria | undefined) => {
      let dateType;
      if (name === "dateTypeCode") {
        dateType = value;
      } else if (name === "inventoryModifiedDate") {
        handleRequestSort("-inventoryModifiedDate", true);
      } else {
        dateType = filters?.dateTypeCode;
      }
      let adjustedFilters: InventoryModifySearchCriteria = {
        ...filters,
        [name]: formattedValue,
        dateTypeCode: dateType,
      };
      adjustedFilters = removeInvalidProperties(adjustedFilters).object;
      return adjustedFilters;
    };

    setFiltersApplied(true);
    setFilters(updatedFilters);
  };
  useEffect(() => {
    bodyRef?.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [rowsPerPage]);
  useEffect(() => {
    const getCount = async () => {
      const count = await getRowsPerPageSetting();
      setRowsPerPage(count);
    };

    getCount();
  }, [getRowsPerPageSetting]);

  useEffect(() => {
    const init = async () => {
      await loadSettings();
      resetSelectedModifyItem();
      setSettingsLoaded(true);
    };
    setAdvanceSearch(false);
    init();
  }, []);

  useEffect(() => {
    const getColumns = async () => {
      const { gridColumns, gridHiddenColumns } = await getColumnsDefinition();
      setColumns(gridColumns);
      setHiddenColumns(gridHiddenColumns);
    };
    getColumns();
  }, [getColumnsDefinition]);

  useEffect(() => {
    let sortColumnChanged = false;
    if (sortColumn) {
      const sortColumnName = sortColumn.replace("-", "");
      hiddenColumns.forEach((c) => {
        const mappedName = getDatabaseSortColumnName(c);
        if (mappedName === sortColumnName) {
          setSortColumn(undefined);
          sortColumnChanged = true;
          return;
        }
      });
    }

    const updatedFilters = removeInvalidProperties({ ...filters });
    if (sortColumnChanged || updatedFilters.deleteCount || data === undefined) {
      setFilters((filters: InventoryModifySearchCriteria | undefined) => updatedFilters.object);
    }
  }, [hiddenColumns]);

  useEffect(() => {
    handleColumnsModified(columns);
  }, [hiddenColumns]);

  useEffect(() => {
    const exportedCols = _orderBy(columns.filter(c => !hiddenColumns.includes(c.field as keyof InventoryModifyUI)), "position")
      .map(c => c.field);

    setExportedColumns(exportedCols);
  }, [columns, hiddenColumns]);


  useEffect(() => {
    if (!filters) {
      setInitialLoad(false);
      return;
    }

    const baseRequiredFields: Array<keyof InventoryModifySearchCriteria> = ["listCount", "requestedPage", "sortColumn"];
    const advanceRequiredFields: Array<keyof InventoryModifySearchCriteria> = [
      ...baseRequiredFields,
      "dateTypeCode",
    ];

    const filterKeys = Object.keys(filters) as Array<keyof InventoryModifySearchCriteria>;
    const extraFields = filterKeys.filter(
      (key) => !baseRequiredFields.includes(key) && !advanceRequiredFields.includes(key),
    );
    const areExtraFieldsEmpty = extraFields.every((key) => filters[key] === "" || filters[key] == null);

    if (isAdvanceSearch) {
      if (
        filterKeys.length >= advanceRequiredFields.length &&
        advanceRequiredFields.every((field) => filterKeys.includes(field)) &&
        areExtraFieldsEmpty
      ) {
        setInitialLoad(true);
      } else if (readyToSearch) {
        setInitialLoad(true);
      } else {
        setInitialLoad(false);
      }
    } else {
      if (
        filterKeys.length >= baseRequiredFields.length &&
        baseRequiredFields.every((field) => filterKeys.includes(field)) &&
        areExtraFieldsEmpty
      ) {
        setInitialLoad(true);
      } else {
        setInitialLoad(false);
      }
    }
  }, [
    filters,
    page,
    sortColumn,
    filtersApplied,
    settingsLoaded,
    columns,
    rowsPerPage,
    partialFilterUpdate,
    isAdvanceSearch,
    readyToSearch,
    refreshData,
  ]);

  const handleColumnResize = (column: keyof InventoryModifyUI, newWidth: number) => {
    if (column && newWidth > 0) {
      setColumnDefinition([column], "width", newWidth);
        setColumns((prevColumns) =>
        prevColumns.map((col) =>
          col.field === column ? { ...col, width: newWidth } : col
        )
      );
    }
  };
  const handleUpdateHiddenColumns = (columns: (keyof InventoryModifyUI)[]) => {
    setHiddenColumns(columns);
  };

  const handleExport = async (allFiles: boolean) => {
    let searchCriteria: InventoryModifySearchCriteria = {
      ...getFileSearchCriteria(),
    };
    searchCriteria.listCount = 0;
    const exportCriteria: InventoryModifyExportCriteria = {
      InventoryModifyDetailIDLists: allFiles
        ? []
        : selectedModifyItems
          .map((items: InventoryModifyUI) => items.inventoryModifyDetailID)
          .filter((id): id is number => id !== undefined),
      ExportColumns: exportedColumns,
      ModifyDetailSearchCriteria: (selectedModifyItems.length > 0 && !allFiles) ? {} : searchCriteria
    };
    await exportFiles(exportCriteria);
    setOpenExportDialog(false);
  };


  const handleColumnsModified = (columns: IColumn[]) => {
    const exportedCols = columns.filter(c => !hiddenColumns.includes(c.field as keyof InventoryModifyUI))
      .map(c => c.field);
    setExportedColumns(exportedCols);
  };

  const handleColumnsReordered = (reorderedColumns: StewartTableColumn<InventoryModifyUI>[]) => {
    const updatedColumns = columns.map((col) => {
      const newPosition = reorderedColumns.findIndex((rc) => String(rc.field) === col.field);
      if (newPosition !== -1) {
        return { ...col, position: newPosition };
      }
      return col;
    });
    setColumns(updatedColumns);
  };

  const visibleColumns = columns
    .filter((c) => c.hideable)
    .map((c) => ({
      field: c.field as keyof InventoryModifyUI,
      name: c.name,
      checked: !hiddenColumns.includes(c.field as keyof InventoryModifyUI),
    }));

  const removeInvalidProperties = (object: { [key: string]: any }) => {
    let deleteCount = 0;
    Object.keys(object).forEach((key) => {
      // Remove null properties and fields that are hidden,
      // with the exception of dateRangeStart which can always be searched even if hidden.
      if (
        object[key] === null ||
        object[key] === undefined ||
        (!isAdvSearchOpen &&
          key !== "dateRangeStart" &&
          hiddenColumns.find((c) => c === key || getSearchField(c) === key))
      ) {
        delete object[key];
        deleteCount++;
      }
    });

    return { object, deleteCount };
  };
  useEffect(() => {
    delete filters?.dateRangeStart;
    delete filters?.dateRangeEnd;
  }, [isAdvanceSearch]);

  useEffect(() => {
    if (requestError === "TIMEOUT") {
      setNoRecordsMessage([TimeoutMessage]);
      setData([]);
    }
  }, [requestError]);

  useEffect(() => {
    if (requestError === "TIMEOUT") {
      setEmptyStateType(EmptyStateType.DEFAULT);
      setNoRecordsMessage([TimeoutMessage]);
      setData([]);
    }
  }, [requestError]);

  return (
    <>
      <StyledBoxContainer className="image-archive">
        <PageSection title="">
          <>
            <TableGridHeaderContainer>

              <SubHeadingText>
                Below are the historical listings of all assignments. Search in the columns
                below or click the Advanced button to filter the results.
              </SubHeadingText>

              <ButtonContainer>
                <InventoryModifyHeader
                  columns={visibleColumns}
                  hiddenColumns={hiddenColumns}
                  updateHiddenColumns={handleUpdateHiddenColumns}
                  handleAdvancedFilterChange={handleAdvancedFilterChange}
                  handleAdvanceSearchPanelOpen={handleAdvanceSearchPanelOpen}
                  onExport={handleExport}
                  setOpenExportDialog={setOpenExportDialog}
                />
              </ButtonContainer>
            </TableGridHeaderContainer>
            <InventoryModifyGrid
              performSearch={performSearch}
              loadingProgressPercent={loadingProgressPercent}
              colsConfig={columns}
              rows={data ?? []}
              rowsPerPage={rowsPerPage}
              noRowsMessage={noRecordsMessage}
              emptyStateType={emptyStateType}
              page={page}
              hiddenColumns={hiddenColumns}
              onFiltersChange={handleFilterChange}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              onColumnResize={handleColumnResize}
              onColumnsModified={handleColumnsReordered}
              selectionConfig={selectionConfig}
              stateList={stateList}
              statusList={statusList}
              policyTypeList={policyTypeList}
              prefixList={prefixList}
            />
            <ScrollToTopArrow refreshSize={data} />
            <FileExportDialog
              open={openExportDialog}
              onCancel={() => setOpenExportDialog(false)}
              onExport={handleExport}
              showSelectionPrompt={selectedModifyItems?.some((s) => s)}
              selectionPrompt={"Include only the selected policies or all policies?"}
              icon={IosShareIcon}
            />

          </>
        </PageSection>
      </StyledBoxContainer>
    </>
  );
};

export default InventoryModify;
